import { RefObject, useEffect, useState } from 'react';

/**
 * A hook that uses ResizeObserver to track the width of a referenced element.
 *
 * @param ref - A React RefObject pointing to the HTML element to observe.
 * @returns The width of the observed element in pixels.
 */
export const useResizeObserver = (ref: RefObject<HTMLElement | null>, deps: any[] = []): number => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      // We are only observing one element, so we can grab the first entry.
      if (entries.length > 0 && entries[0].contentBoxSize.length > 0) {
        const contentBoxSize = entries[0].contentBoxSize[0];
        setWidth(contentBoxSize.inlineSize);
      }
    });

    observer.observe(element);

    return () => {
      // The observer instance is tied to this effect, so we disconnect
      // from all elements when the effect cleans up.
      observer.disconnect();
    };
  }, [ref.current, ...deps]); // Rerun the effect if the referenced element changes.

  return width;
};
